import { Icon } from "@fluentui/react";
import React from "react";
import {
    ButtonRenderProps,
    ContentSection,
    getUsernameWithoutPrefix,
    Grid,
    GridColumn,
    GridRow,
    Label,
    LabeledDate,
    LabeledScrollableText,
    LabeledText
} from "sprint-common-web";

import {
    DeliveryInfoButtonAside,
    DeliveryInfoButtonAsideProps
} from "../../../../../common/components/delivery-info-button-aside/DeliveryInfoButtonAside";
import { LabeledRecordTypeBadge } from "../../../../../common/components/RecordTypeBadge";
import { LabeledTrailerInfoBadge } from "../../../../../common/components/trailer-info/LabeledTrailerInfoBadge";
import { DeliveryType } from "../../../../../common/types/DeliveryType";
import { renderCloudFileStatusDetailsText } from "../../../common/utils/renderCloudFileStatus";
import { useCloudFileDetails } from "../../common/useCloudFileDetails";
import { ApproveCloudFileDetailsNotificationsButton } from "./buzz/approve-notifications/ApproveCloudFileDetailsNotificationsButton";
import { CloudFileInfoStatusButton, CloudFileInfoStatusButtonProps } from "./CloudFileInfoStatusButton";

export interface CloudFileQcProps {
    minHeight: number;
}
export const CloudFileInfoSection = ({ minHeight }: CloudFileQcProps): JSX.Element => {
    const { data: cloudFileDetails, isLoading: isCloudFileDetailsLoading } = useCloudFileDetails();

    if (!cloudFileDetails || isCloudFileDetailsLoading) {
        return <ContentSection header="Info" isLoading={isCloudFileDetailsLoading} style={{ height: minHeight }} />;
    }

    const approveNotificationsButton: ButtonRenderProps = {
        render: ApproveCloudFileDetailsNotificationsButton
    };

    const deliveryInfoButton: ButtonRenderProps<DeliveryInfoButtonAsideProps> = {
        itemId: cloudFileDetails.deliveryId,
        isLabeled: true,
        render: DeliveryInfoButtonAside
    };

    const cloudFileStatusButton: ButtonRenderProps<CloudFileInfoStatusButtonProps> = {
        render: CloudFileInfoStatusButton,
        cloudFileDetails
    };

    const lastUpdatedBy = getUsernameWithoutPrefix(cloudFileDetails.lastUpdatedBy) || "-";

    return (
        <ContentSection
            header="Info"
            buttons={[approveNotificationsButton, cloudFileStatusButton, deliveryInfoButton]}
            isLoading={isCloudFileDetailsLoading}
            style={{ height: minHeight }}
        >
            <Grid>
                <GridRow>
                    <GridColumn lg={7}>
                        <LabeledRecordTypeBadge value={cloudFileDetails.recordType} />
                        <LabeledText label="File path" value={cloudFileDetails.filePath} textTooltip maxLength={35} />
                        <LabeledText label="Originator" value={cloudFileDetails.originator} />
                        <LabeledText label="Delivery type" value={DeliveryType[cloudFileDetails.deliveryType]} />
                        <LabeledText
                            label="Delivery file name"
                            value={cloudFileDetails.fileName}
                            textTooltip
                            maxLength={35}
                        />
                        {renderCloudFileStatusDetailsText(cloudFileDetails.statusDetails)}
                    </GridColumn>
                    <GridColumn lg={5}>
                        {cloudFileDetails.isTrailer && <LabeledTrailerInfoBadge />}
                        <LabeledDate label="Arrival date" value={new Date(cloudFileDetails.arrivalDate)} />
                        <LabeledText label="Last updated by" value={lastUpdatedBy} maxLength={35} textTooltip={true} />
                        <LabeledText
                            label="Sender"
                            value={cloudFileDetails.sender?.name}
                            textTooltip={true}
                            maxLength={35}
                        />
                        <LabeledText label="Subject" value={cloudFileDetails.sender?.subject} textTooltip={true} />
                        <Label>Notifications approved</Label>
                        {cloudFileDetails.isNotificationApproved ? (
                            <Icon iconName="Accept" />
                        ) : (
                            <Icon iconName="Cancel" />
                        )}
                        <LabeledScrollableText
                            height={60}
                            width={"auto"}
                            label="Message"
                            value={cloudFileDetails.sender?.message ?? "-"}
                        />
                    </GridColumn>
                </GridRow>
            </Grid>
        </ContentSection>
    );
};
